import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
    Alert,
    Button,
    Card,
    CardBody,
    Progress,
} from 'reactstrap';
import * as FileSaver from 'file-saver';
import * as XLSX from '@e965/xlsx';
import moment from 'moment';
import Loader from '../Loader/Loader';
import SyncFileImportSection from '../SynchronisationPanel/SyncFileImportSection/SyncFileImportSection';
import Utils from '../../services/Utils';
import {
    FILE_EXTENSIONS,
    PROMO_CODE_ARTICLE_TYPE,
    buildArticlePayloads,
    buildPromoCodeInputHelpItems,
    buildSampleWorkbook,
    preparePromoCodesImport,
    readPromoCodesFile,
    salepointDisplayName,
} from '../../services/promoCodesImport';

import './PromoCodesImporter.css';

/**
 * Promo-code import UI hosted in the Synchronisation panel.
 *
 * Flow: upload file → download sample (all POS) → validate/preview → create articles.
 * Redux modules and Articles API are loaded lazily when the tab becomes visible
 * to avoid circular init with the sync panel barrel.
 *
 * @param {{ visible: boolean }} props
 */
const PromoCodesImporter = ({ visible }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();

    const { salepoints } = useSelector(state => state.salepoints);
    const { articlecategories } = useSelector(state => state.articlecategories);
    const { setting } = useSelector(state => state.settings);

    const [fileName, setFileName] = useState('');
    const [groups, setGroups] = useState([]);
    const [validation, setValidation] = useState(null);
    const [existingCodes, setExistingCodes] = useState([]);
    const [isLoadingCodes, setIsLoadingCodes] = useState(false);
    const [pendingFile, setPendingFile] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const [importProgress, setImportProgress] = useState({ done: 0, total: 0 });
    const [importResults, setImportResults] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [detailsIndex, setDetailsIndex] = useState(0);

    useEffect(() => {
        if (!visible)
            return undefined;

        let cancelled = false;

        const bootstrap = async () => {
            // Dynamic imports avoid circular dependency with SynchronisationPanel barrels.
            const [
                { loadSalepoints },
                { loadArticlecategories },
            ] = await Promise.all([
                import('../../redux/modules/salepoints'),
                import('../../redux/modules/articlecategories'),
            ]);

            if (cancelled)
                return;

            dispatch(loadSalepoints());
            dispatch(loadArticlecategories());
        };

        bootstrap();
        return () => { cancelled = true; };
    }, [dispatch, visible]);

    useEffect(() => {
        if (!visible)
            return undefined;

        let cancelled = false;

        const loadExistingCodes = async () => {
            setIsLoadingCodes(true);
            try {
                // Used to reject discount codes that already exist in the instance.
                const ArticlesApi = (await import('../../services/rest/Articles')).default;
                const data = await ArticlesApi.list({
                    types: PROMO_CODE_ARTICLE_TYPE,
                    fields: ['_id', 'variants'],
                });
                if (cancelled)
                    return;

                const codes = (data.articles || []).flatMap(article =>
                    (article.variants || [])
                        .map(variant => variant?.discount?.code)
                        .filter(Boolean)
                );
                setExistingCodes(codes);
            } catch (_err) {
                if (!cancelled)
                    setExistingCodes([]);
            } finally {
                if (!cancelled)
                    setIsLoadingCodes(false);
            }
        };

        loadExistingCodes();
        return () => { cancelled = true; };
    }, [visible]);

    const categoryIds = useMemo(
        () => (articlecategories || []).map(category => category._id),
        [articlecategories]
    );

    const categoryNameById = useMemo(() => {
        const map = new Map();
        (articlecategories || []).forEach(category => {
            map.set(
                category._id,
                Utils.localized_or_fallback(category.name, i18n.language) || category._id
            );
        });
        return map;
    }, [articlecategories, i18n.language]);

    const categorySummaries = useMemo(() => {
        const byCategory = new Map();

        groups.forEach(group => {
            const categoryId = group.rows[0]?.category || '';
            if (!byCategory.has(categoryId))
                byCategory.set(categoryId, { articles: 0, variants: 0 });
            const entry = byCategory.get(categoryId);
            entry.articles += 1;
            entry.variants += group.rows.length;
        });

        return [...byCategory.entries()].map(([categoryId, counts]) => ({
            categoryId,
            categoryName: categoryNameById.get(categoryId)
                || t('articles.promo_codes_import.preview_unknown_category'),
            ...counts,
        }));
    }, [groups, categoryNameById, t]);

    const promoInputHelpItems = useMemo(() => {
        if (!visible || !setting)
            return [];

        return buildPromoCodeInputHelpItems(setting, salepoints || [], i18n.language);
    }, [visible, setting, salepoints, i18n.language]);

    const currency = Utils.currency();

    const downloadSample = () => {
        const allSalepoints = salepoints || [];
        const workbook = buildSampleWorkbook({
            salepoints: allSalepoints,
            lang: i18n.language,
            stockType: allSalepoints.length ? 'article' : 'none',
        });
        const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        FileSaver.saveAs(
            new Blob([buffer], { type: 'application/octet-stream' }),
            'promo_codes_import.xlsx'
        );
    };

    const resetPreview = () => {
        setGroups([]);
        setValidation(null);
        setImportResults(null);
        setUploadError(null);
        setFileName('');
        setPendingFile(null);
        setImportProgress({ done: 0, total: 0 });
        setDetailsIndex(0);
    };

    const processFile = useCallback(async (file, codes) => {
        const extension = file.name.split('.').pop()?.toLowerCase();
        if (!FILE_EXTENSIONS.includes(extension)) {
            setUploadError(t('articles.promo_codes_import.unsupported_file_type'));
            setFileName('');
            setGroups([]);
            setValidation(null);
            setPendingFile(null);
            return;
        }

        try {
            const rows = await readPromoCodesFile(file);
            const prepared = preparePromoCodesImport({
                rows,
                salepoints: salepoints || [],
                lang: i18n.language,
                categoryIds,
                existingCodes: codes,
            });

            setFileName(file.name);
            setGroups(prepared.groups);
            setValidation(prepared.validation);
            setDetailsIndex(0);
            setPendingFile(null);
        } catch (err) {
            const messageKey = err?.message?.startsWith('articles.')
                ? err.message
                : 'articles.promo_codes_import.invalid_file_content';
            setUploadError(t(messageKey));
            setFileName('');
            setGroups([]);
            setValidation(null);
            setPendingFile(null);
        }
    }, [salepoints, i18n.language, categoryIds, t]);

    const onFileSelected = useCallback(async file => {
        setUploadError(null);
        setImportResults(null);
        setDetailsIndex(0);

        if (!file)
            return;

        // Existing codes are loaded in background; defer validation until ready.
        if (isLoadingCodes) {
            setPendingFile(file);
            setFileName(file.name);
            setGroups([]);
            setValidation(null);
            return;
        }

        await processFile(file, existingCodes);
    }, [isLoadingCodes, existingCodes, processFile]);

    useEffect(() => {
        if (!pendingFile || isLoadingCodes)
            return undefined;

        const file = pendingFile;
        setPendingFile(null);
        processFile(file, existingCodes);
        return undefined;
    }, [pendingFile, isLoadingCodes, existingCodes, processFile]);

    const runImport = async () => {
        if (!validation?.valid || !groups.length || isImporting)
            return;

        const ArticlesApi = (await import('../../services/rest/Articles')).default;
        // Batch marker stored on each created article (`opaque.imported_at`).
        const importedAt = moment().format('YYYYMMDDHHmmss');
        const payloads = buildArticlePayloads(groups, {
            currency: Utils.currency(),
            langs: Utils.langs(),
            vat: setting?.customer?.tax?.vat_articles || 0,
            importedAt,
        });

        setIsImporting(true);
        setImportProgress({ done: 0, total: payloads.length });
        setImportResults(null);

        const created = [];
        const failed = [];

        for (let index = 0; index < payloads.length; index += 1) {
            const payload = payloads[index];
            try {
                const data = await ArticlesApi.create(payload);
                created.push({
                    name: payload.name?.[Utils.default_lang()] || payload.name?.fr || groups[index]?.name,
                    articleId: data.article?._id,
                    variants: payload.variants.length,
                });
            } catch (err) {
                failed.push({
                    name: payload.name?.[Utils.default_lang()] || payload.name?.fr || groups[index]?.name,
                    message: err?.message || String(err),
                });
            }
            setImportProgress({ done: index + 1, total: payloads.length });
        }

        setImportResults({ created, failed, importedAt });
        setIsImporting(false);

        if (created.length) {
            try {
                const data = await ArticlesApi.list({
                    types: PROMO_CODE_ARTICLE_TYPE,
                    fields: ['_id', 'variants'],
                });
                const codes = (data.articles || []).flatMap(article =>
                    (article.variants || [])
                        .map(variant => variant?.discount?.code)
                        .filter(Boolean)
                );
                setExistingCodes(codes);
            } catch (_err) {
                // ignore refresh errors
            }
        }
    };

    const formatStocks = useCallback(row => {
        if (!row.stocks?.length)
            return t('articles.promo_codes_import.preview_no_stock');

        return row.stocks.map(stock => {
            const salepoint = (salepoints || []).find(item => item._id === stock.salepointId);
            const name = salepoint
                ? salepointDisplayName(salepoint, i18n.language)
                : stock.salepointId;
            return `${name}: ${stock.availability}`;
        }).join(' · ');
    }, [salepoints, i18n.language, t]);

    const articlePreview = useMemo(() => {
        if (!groups.length)
            return null;

        const safeIndex = Math.min(detailsIndex, groups.length - 1);
        const group = groups[safeIndex];
        if (!group)
            return null;

        const first = group.rows[0];
        const categoryName = categoryNameById.get(first?.category)
            || first?.category
            || t('articles.promo_codes_import.preview_unknown_category');

        const navButtons = (
            <div className="d-flex gap-2 justify-content-between align-items-center mb-2">
                <span className="PromoCodesImporter-navLabel">
                    {t('articles.promo_codes_import.preview_nav', {
                        current: safeIndex + 1,
                        total: groups.length,
                    })}
                </span>
                <div className="d-flex gap-2">
                    <Button
                        color="info"
                        size="sm"
                        disabled={safeIndex === 0}
                        onClick={() => setDetailsIndex(safeIndex - 1)}
                    >
                        <i className="fa fa-arrow-left" />
                    </Button>
                    <Button
                        color="info"
                        size="sm"
                        disabled={safeIndex >= groups.length - 1}
                        onClick={() => setDetailsIndex(safeIndex + 1)}
                    >
                        <i className="fa fa-arrow-right" />
                    </Button>
                </div>
            </div>
        );

        return (
            <div className="PromoCodesImporter-articlePreview">
                {navButtons}
                <table className="table w-100 PromoCodesImporter-detailsTable">
                    <tbody>
                        <tr>
                            <th>{t('articles.promo_codes_import.col_number')}</th>
                            <td>{safeIndex + 1}</td>
                        </tr>
                        <tr>
                            <th>{t('articles.promo_codes_import.col_name')}</th>
                            <td>{group.name}</td>
                        </tr>
                        <tr>
                            <th>{t('articles.promo_codes_import.col_category')}</th>
                            <td>{categoryName}</td>
                        </tr>
                        <tr>
                            <th>{t('articles.promo_codes_import.col_stock_type')}</th>
                            <td>{first?.stockType}</td>
                        </tr>
                        <tr>
                            <th>{t('articles.promo_codes_import.col_variants')}</th>
                            <td>{group.rows.length}</td>
                        </tr>
                        <tr>
                            <th>{t('articles.promo_codes_import.col_codes')}</th>
                            <td>{group.rows.map(row => row.code).join(', ')}</td>
                        </tr>
                    </tbody>
                </table>

                {group.rows.map((row, variantIndex) => (
                    <div key={`${row.code}-${row.line}`} className="PromoCodesImporter-variantBlock">
                        <h6>
                            {t('articles.promo_codes_import.preview_variant', { index: variantIndex + 1 })}
                        </h6>
                        <table className="table w-100 PromoCodesImporter-detailsTable">
                            <tbody>
                                <tr>
                                    <th>{t('articles.promo_codes_import.col_code')}</th>
                                    <td>{row.code}</td>
                                </tr>
                                <tr>
                                    <th>{t('articles.promo_codes_import.col_percentage')}</th>
                                    <td>{row.percentage}</td>
                                </tr>
                                <tr>
                                    <th>{t('articles.promo_codes_import.col_fixed')}</th>
                                    <td>{row.fixed} {currency}</td>
                                </tr>
                                <tr>
                                    <th>{t('articles.promo_codes_import.col_target')}</th>
                                    <td>
                                        {row.applyLast
                                            ? t('articles.promo_codes_import.target_last_item')
                                            : t('articles.promo_codes_import.target_cart')}
                                    </td>
                                </tr>
                                <tr>
                                    <th>{t('articles.promo_codes_import.col_stocks')}</th>
                                    <td>{formatStocks(row)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                ))}
                {navButtons}
            </div>
        );
    }, [
        groups,
        detailsIndex,
        categoryNameById,
        t,
        currency,
        formatStocks,
    ]);

    if (!visible)
        return null;

    if (!salepoints || !articlecategories || !setting)
        return <Loader />;

    const canImport = !!validation?.valid && groups.length > 0 && !isImporting;
    const totalCodes = groups.reduce((sum, group) => sum + group.rows.length, 0);
    const isPreparingFile = !!pendingFile && isLoadingCodes;

    const uploadHelp = (
        <>
            <p>
                <Trans
                    i18nKey="articles.promo_codes_import.upload_help"
                    components={{
                        code: <code className="SyncFileImportSection-code" />,
                    }}
                />
            </p>
            {promoInputHelpItems.length > 0 && (
                <ul className="SyncFileImportSection-policyList">
                    {promoInputHelpItems.map((item, index) => (
                        <li key={`${item.key}-${index}`} className="SyncFileImportSection-policy">
                            <i className="fa fa-info-circle" aria-hidden="true" />
                            <span>{ t(item.key, item.params) }</span>
                        </li>
                    ))}
                </ul>
            )}
        </>
    );

    return (
        <div className="PromoCodesImporter">
            <SyncFileImportSection
                title={ t('articles.promo_codes_import.upload_title') }
                help={ uploadHelp }
                formats={ t('common.supported_spreadsheet_formats') }
                accept=".csv,.xls,.xlsx,.ods,.numbers"
                disabled={ isImporting }
                fileName={ fileName }
                onFile={ onFileSelected }
                downloadSlot={
                    <Button color="info" size="sm" onClick={downloadSample}>
                        <i className="fa fa-download" />&nbsp;{t('articles.promo_codes_import.download_sample')}
                    </Button>
                }
            >
                {(groups.length > 0 || uploadError || pendingFile) && (
                    <Button color="secondary" size="sm" className="mt-3" onClick={resetPreview} disabled={isImporting}>
                        {t('articles.promo_codes_import.reset')}
                    </Button>
                )}
                {isPreparingFile && (
                    <p className="text-muted small mt-3 mb-0">
                        {t('articles.promo_codes_import.loading_existing_codes')}
                    </p>
                )}
                {uploadError && <Alert color="danger" className="mt-3 mb-0">{uploadError}</Alert>}
            </SyncFileImportSection>

            {validation && (
                <Card className="mb-3">
                    <CardBody>
                        <h5>{t('articles.promo_codes_import.preview_title')}</h5>
                        <p>
                            {t('articles.promo_codes_import.preview_summary', {
                                articles: groups.length,
                                codes: totalCodes,
                            })}
                        </p>

                        {categorySummaries.length > 0 && (
                            <div className="PromoCodesImporter-categorySummary mb-3">
                                <strong>{t('articles.promo_codes_import.preview_category_summary_title')}</strong>
                                <ul className="mb-0 mt-2">
                                    {categorySummaries.map(summary => (
                                        <li key={summary.categoryId || 'unknown'}>
                                            {t('articles.promo_codes_import.preview_category_summary_line', {
                                                category: summary.categoryName,
                                                articles: summary.articles,
                                                variants: summary.variants,
                                            })}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {validation.errors.length > 0 && (
                            <Card className="mb-3 PromoCodesImporter-messagesCard PromoCodesImporter-messagesCard--danger">
                                <CardBody>
                                    <strong>
                                        {t('articles.promo_codes_import.errors_title')}
                                        {' '}({validation.errors.length})
                                    </strong>
                                    <div className="PromoCodesImporter-messagesList mt-2">
                                        <ul className="mb-0">
                                            {validation.errors.map((error, index) => (
                                                <li key={`err-${index}`}>{t(error.key, error.params)}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </CardBody>
                            </Card>
                        )}

                        {validation.warnings.length > 0 && (
                            <Card className="mb-3 PromoCodesImporter-messagesCard PromoCodesImporter-messagesCard--warning">
                                <CardBody>
                                    <strong>
                                        {t('articles.promo_codes_import.warnings_title')}
                                        {' '}({validation.warnings.length})
                                    </strong>
                                    <div className="PromoCodesImporter-messagesList mt-2">
                                        <ul className="mb-0">
                                            {validation.warnings.map((warning, index) => (
                                                <li key={`warn-${index}`}>{t(warning.key, warning.params)}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </CardBody>
                            </Card>
                        )}

                        {validation.valid && (
                            <Alert color="success" className="mb-3">
                                {t('articles.promo_codes_import.ready')}
                            </Alert>
                        )}

                        {articlePreview}

                        <Button color="success" className="mt-2" onClick={runImport} disabled={!canImport}>
                            <i className="fa fa-upload" />&nbsp;{t('articles.promo_codes_import.do_import')}
                        </Button>
                    </CardBody>
                </Card>
            )}

            {(isImporting || importResults) && (
                <Card className="mb-3">
                    <CardBody>
                        <h5>{t('articles.promo_codes_import.result_title')}</h5>
                        {isImporting && (
                            <>
                                <p>
                                    {t('articles.promo_codes_import.importing', {
                                        done: importProgress.done,
                                        total: importProgress.total,
                                    })}
                                </p>
                                <Progress
                                    value={importProgress.total ? (importProgress.done / importProgress.total) * 100 : 0}
                                />
                            </>
                        )}
                        {importResults && (
                            <>
                                <Alert color={importResults.failed.length ? 'warning' : 'success'}>
                                    {t('articles.promo_codes_import.result_summary', {
                                        created: importResults.created.length,
                                        failed: importResults.failed.length,
                                        importedAt: importResults.importedAt,
                                    })}
                                </Alert>
                                {importResults.failed.length > 0 && (
                                    <ul>
                                        {importResults.failed.map((item, index) => (
                                            <li key={`fail-${index}`}>
                                                <strong>{item.name}</strong>: {item.message}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {importResults.created.length > 0 && (
                                    <Button tag={Link} to="/articles" color="primary" size="sm">
                                        {t('articles.promo_codes_import.back_to_list')}
                                    </Button>
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>
            )}
        </div>
    );
};

export default PromoCodesImporter;
